import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock, Send } from 'lucide-react';
import { PageSEO } from '@/components/SEO';
import axios from 'axios';

const FORMSPREE_ID = process.env.REACT_APP_FORMSPREE_ID;

function getApiBase() {
  const url = process.env.REACT_APP_BACKEND_URL;
  if (url && url !== 'undefined') return `${url.replace(/\/$/, '')}/api`;
  if (typeof window !== 'undefined') return `${window.location.origin}/api`;
  return '/api';
}

export default function Kontakt() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [phoneError, setPhoneError] = useState(null);

  const validatePhone = (phone) => {
    if (!phone) return true;
    const digits = phone.replace(/\D/g, '');
    return digits.length === 8;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setPhoneError(null);

    if (formData.phone && !validatePhone(formData.phone)) {
      setPhoneError('Telefonnummer må ha nøyaktig 8 siffer.');
      setIsSubmitting(false);
      return;
    }

    try {
      // Get reCAPTCHA token
      let recaptcha_token = null;
      if (window.grecaptcha) {
        try {
          recaptcha_token = await new Promise((resolve, reject) => {
            window.grecaptcha.ready(() => {
              window.grecaptcha.execute('6LdMuo0sAAAAAJGFT2Wi257JosNQSehbrGsYtsXo', { action: 'contact_submit' })
                .then(resolve)
                .catch(reject);
            });
          });
        } catch (recaptchaErr) {
          console.error('reCAPTCHA error:', recaptchaErr);
        }
      }

      if (FORMSPREE_ID) {
        const response = await axios.post(
          `https://formspree.io/f/${FORMSPREE_ID}`,
          {
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            company: formData.company,
            service: formData.service,
            message: formData.message,
            _subject: `Ny henvendelse fra ${formData.name}`,
            'g-recaptcha-response': recaptcha_token
          },
          { headers: { 'Content-Type': 'application/json', Accept: 'application/json' } }
        );
        if (response.data?.ok) {
          navigate('/takk');
        } else {
          setError(response.data?.error || 'Noe gikk galt. Vennligst prøv igjen.');
        }
      } else {
        const apiBase = getApiBase();
        const response = await axios.post(`${apiBase}/contact`, {
          ...formData,
          recaptcha_token
        });
        if (response.data?.status === 'success') {
          navigate('/takk');
        } else {
          setError(response.data?.message || 'Noe gikk galt. Vennligst prøv igjen.');
        }
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      if (!FORMSPREE_ID) {
        setError('Kontaktskjema er ikke konfigurert. Vennligst ring oss på 400 81 956 eller send e-post til post@sidekraft.no.');
      } else {
        setError('Kunne ikke sende meldingen. Vennligst prøv igjen eller kontakt oss på post@sidekraft.no.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const kontaktInfo = [
    {
      icon: Phone,
      title: 'Telefon',
      value: '+47 400 81 956',
      href: 'tel:+4740081956',
    },
    {
      icon: Mail,
      title: 'E-post',
      value: 'post@sidekraft.no',
      href: 'mailto:post@sidekraft.no',
    },
    {
      icon: MapPin,
      title: 'Adresse',
      value: 'Oslo, Norge',
      href: null,
    },
    {
      icon: Clock,
      title: 'Åpningstider',
      value: 'Man-Fre: 09:00-17:00',
      href: null,
    },
  ];

  const tjenester = [
    'Ny nettside',
    'SEO',
    'Annonsering',
    'Nettbutikk',
    'Logodesign',
    'Google Bedriftsprofil',
    'Annet',
  ];

  return (
    <div>
      <PageSEO
        title="Kontakt"
        path="/kontakt"
        description="Kontakt Side Kraft for nettside, SEO eller digital markedsføring. Ring 400 81 956 eller send melding. Svar innen 24 timer. Oslo, Norge."
      />
      {/* Hero Section */}
      <section className="pt-16 md:pt-20 pb-16 md:pb-20 bg-muted" data-testid="kontakt-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <p className="font-manrope text-xs font-medium tracking-[0.3em] uppercase text-accent mb-4">
              Ta kontakt
            </p>
            <h1 className="font-syne font-bold text-5xl md:text-6xl text-primary tracking-tight leading-[0.95]">
              La oss snakke om din digitale vekst
            </h1>
            <p className="font-manrope text-lg text-muted-foreground mt-8 leading-relaxed">
              Vi er alltid klare for en uforpliktende samtale. Fortell oss om dine mål og utfordringer, så finner vi ut hvordan vi kan hjelpe.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 md:py-32" data-testid="kontakt-form-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="font-syne font-bold text-2xl text-primary mb-8">
                Kontaktinformasjon
              </h2>
              
              <div className="space-y-6">
                {kontaktInfo.map((info, index) => (
                  <div key={index} className="flex items-start gap-4" data-testid={`contact-info-${index}`}>
                    <div className="w-12 h-12 bg-muted flex items-center justify-center flex-shrink-0">
                      <info.icon className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <p className="font-manrope text-xs font-medium tracking-wide uppercase text-muted-foreground mb-1">
                        {info.title}
                      </p>
                      {info.href ? (
                        <a 
                          href={info.href}
                          onClick={(e) => {
                            e.preventDefault();
                            if (typeof window.gtag_report_conversion === 'function') {
                              window.gtag_report_conversion(info.href);
                            } else {
                              window.location.href = info.href;
                            }
                          }}
                          className="font-manrope text-primary hover:text-accent transition-colors duration-300"
                        >
                          {info.value}
                        </a>
                      ) : (
                        <p className="font-manrope text-primary">{info.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Quick CTA */}
              <div className="mt-12 p-6 bg-primary text-primary-foreground">
                <h3 className="font-syne font-bold text-lg mb-3">
                  Foretrekker du å ringe?
                </h3>
                <p className="font-manrope text-sm text-primary-foreground/70 mb-4">
                  Vi tar gjerne en prat på telefon. Ring oss direkte for en rask samtale.
                </p>
                <a 
                  href="tel:40081956" 
                  onClick={(e) => {
                    e.preventDefault();
                    if (typeof window.gtag_report_conversion === 'function') {
                      window.gtag_report_conversion('tel:40081956');
                    } else {
                      window.location.href = 'tel:40081956';
                    }
                  }}
                  className="inline-flex items-center gap-2 font-syne font-bold text-accent hover:text-primary-foreground transition-colors duration-300"
                  data-testid="quick-call-button"
                >
                  <Phone className="w-4 h-4" />
                  Ring nå
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <form onSubmit={handleSubmit} className="space-y-8" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="font-manrope text-xs font-medium tracking-wide uppercase text-muted-foreground mb-2 block">
                      Fullt navn *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full bg-transparent border-b-2 border-border focus:border-primary px-0 py-4 focus:outline-none focus:ring-0 font-manrope text-lg transition-colors duration-300"
                      required
                      data-testid="contact-name"
                    />
                  </div>
                    <div>
                      <label className="font-manrope text-xs font-medium tracking-wide uppercase text-muted-foreground mb-2 block">
                        E-post *
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full bg-transparent border-b-2 border-border focus:border-primary px-0 py-4 focus:outline-none focus:ring-0 font-manrope text-lg transition-colors duration-300"
                        required
                        data-testid="contact-email"
                      />
                    </div>
                    <div>
                      <label className="font-manrope text-xs font-medium tracking-wide uppercase text-muted-foreground mb-2 block">
                        Telefon (valgfritt, maks 8 siffer)
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 8);
                          setFormData({ ...formData, phone: val });
                          setPhoneError(null);
                        }}
                        maxLength={8}
                        placeholder="12345678"
                        className={`w-full bg-transparent border-b-2 px-0 py-4 focus:outline-none focus:ring-0 font-manrope text-lg transition-colors duration-300 ${phoneError ? 'border-destructive focus:border-destructive' : 'border-border focus:border-primary'}`}
                        data-testid="contact-phone"
                      />
                      {phoneError && (
                        <p className="font-manrope text-sm text-destructive mt-2">{phoneError}</p>
                      )}
                    </div>
                    <div>
                      <label className="font-manrope text-xs font-medium tracking-wide uppercase text-muted-foreground mb-2 block">
                        Bedrift
                      </label>
                      <input
                        type="text"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        className="w-full bg-transparent border-b-2 border-border focus:border-primary px-0 py-4 focus:outline-none focus:ring-0 font-manrope text-lg transition-colors duration-300"
                        data-testid="contact-company"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="font-manrope text-xs font-medium tracking-wide uppercase text-muted-foreground mb-2 block">
                      Hva kan vi hjelpe deg med?
                    </label>
                    <div className="relative">
                      <select
                        value={formData.service}
                        onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                        className="w-full bg-background border-2 border-border focus:border-primary rounded-none px-4 py-4 focus:outline-none focus:ring-0 font-manrope text-base transition-colors duration-300 cursor-pointer"
                        style={{ fontSize: '16px', minHeight: '56px' }}
                        data-testid="contact-service"
                      >
                        <option value="">Velg en tjeneste</option>
                        {tjenester.map((tjeneste) => (
                          <option key={tjeneste} value={tjeneste}>{tjeneste}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="font-manrope text-xs font-medium tracking-wide uppercase text-muted-foreground mb-2 block">
                      Melding *
                    </label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={5}
                      className="w-full bg-transparent border-b-2 border-border focus:border-primary px-0 py-4 focus:outline-none focus:ring-0 font-manrope text-lg resize-none transition-colors duration-300"
                      required
                      placeholder="Fortell oss om ditt prosjekt eller dine behov..."
                      data-testid="contact-message"
                    />
                  </div>

                  {error && (
                    <div className="p-4 bg-destructive/10 border border-destructive rounded">
                      <p className="font-manrope text-sm text-destructive">{error}</p>
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex items-center gap-2 font-syne font-bold text-sm uppercase tracking-wide px-8 py-4 bg-primary text-primary-foreground hover:bg-accent transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="contact-submit"
                  >
                    {isSubmitting ? 'Sender...' : 'Send henvendelse'}
                    <Send className="w-4 h-4" />
                  </button>
                </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
